import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/joy';
import { BRAND } from '../brand';
import { fitLogoFrame } from '../util/locusLogoFit';
import './LocusLogo.css';

const isGoogleChrome =
  typeof navigator !== 'undefined' &&
  /Chrome|CriOS/.test(navigator.userAgent) &&
  !/Edg|OPR|SamsungBrowser|Firefox/.test(navigator.userAgent);

const LocusLogo = ({ width = 300, height = 100, color = 'dark', variant = 'default', sx }) => {
  const frameRef = useRef(null);
  const isNav = variant === 'nav';
  const colorClass = color === 'white' ? 'locus-logo-frame--white' : 'locus-logo-frame--dark';

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    let rafId = null;
    const runFit = () => {
      fitLogoFrame(frame, { shrinkWidth: isNav, compact: isNav, autoHeight: isNav });
    };
    const scheduleFit = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        runFit();
      });
    };

    // Observe parent for nav shrink-to-fit — frame width is written by fitLogoFrame.
    const observeTarget = isNav && frame.parentElement ? frame.parentElement : frame;
    const observer = new ResizeObserver(scheduleFit);
    observer.observe(observeTarget);

    const fitWhenFontsReady = () => {
      scheduleFit();
      if (!document.fonts?.load) return;
      // Chrome may apply Montserrat metrics one frame after fonts.ready.
      document.fonts.load('700 12px Montserrat').finally(scheduleFit);
      document.fonts.load('400 12px Montserrat').finally(scheduleFit);
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(fitWhenFontsReady);
    } else {
      fitWhenFontsReady();
    }

    return () => {
      observer.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [width, height, isNav]);

  return (
    <Box
      ref={frameRef}
      className={`locus-logo-frame ${colorClass}${isNav ? ' locus-logo-frame--nav' : ''}${isGoogleChrome ? ' locus-logo-frame--chrome' : ''}`}
      sx={
        isNav
          ? { width: '100%', maxWidth: width || 240, height: 'auto', ...sx }
          : { width, height, ...sx }
      }
      role="img"
      aria-label={`${BRAND.name}, Since 2002, ${BRAND.tagline}`}
    >
      <div className="logo-box">
        <div className="logo">
          <div className="logo-title">LOCUS DENTAL CARE</div>
          <div className="logo-since">
            <span className="line" aria-hidden="true" />
            <span className="text">Since 2002</span>
            <span className="line" aria-hidden="true" />
          </div>
          <div className="logo-tagline">({BRAND.tagline})</div>
        </div>
      </div>
    </Box>
  );
};

export default LocusLogo;
